import React, { useEffect } from 'react'
import { Box, Button, Grid, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { AddCircleOutline } from '@material-ui/icons'
import { Form, Formik } from 'formik'
import { array, object, string } from 'yup'
import { useDispatch, useSelector } from 'react-redux'

import { STEPS } from '../../../redux/modules/teamOnboarding'
import ButtonSubmit from '../../common/ButtonSubmit'
import { submitTeamMembersStart } from '../../../redux/modules/teamOnboarding'
import SteppedContent from '../../common/SteppedContent'
import {
  publishChangedMemberEmail,
  publishClickedAddAnother,
  publishViewedInviteMembers,
} from '../../../util/events/teamsOnboardingEventPublishers'

const schema = object().shape({
  teamMemberEmails: array().of(string().email('Please enter a valid email address.')),
})

const useStyles = makeStyles({
  textField: {
    width: '320px',
  },
})

const StepTeamMembers = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSettingNextStep = useSelector(state => state.teamOnboarding.isSettingNextStep)
  const errorSettingNextStep = useSelector(state => state.teamOnboarding.errorSettingNextStep)

  const handleSubmit = ({ teamMemberEmails }) => {
    const payload = teamMemberEmails.filter(email => email !== '')
    dispatch(submitTeamMembersStart(payload))
  }

  const handleAddAnother = (values, setFieldValue) => {
    setFieldValue('teamMemberEmails', [...values.teamMemberEmails, ''])
    publishClickedAddAnother()
  }

  const handleBlurEmail = (event, error, handleBlur) => {
    handleBlur(event)
    publishChangedMemberEmail(event.target.value, error)
  }

  useEffect(() => {
    publishViewedInviteMembers()
  }, [])

  return (
    <SteppedContent
      width="720px"
      title="Who else is on your team?"
      description="Invite your team members to join your Foodsby team and start enjoying the benefits."
      progressText={`Step ${STEPS.TEAM_MEMBERS} of 3`}
    >
      <Formik
        initialValues={{ teamMemberEmails: [''] }}
        onSubmit={handleSubmit}
        validationSchema={schema}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <Grid container spacing={3} direction="column" alignItems="center">
              {values.teamMemberEmails.map((email, key) => (
                <Grid key={key} item>
                  <TextField
                    autoFocus
                    name={`teamMemberEmails[${key}]`}
                    className={classes.textField}
                    placeholder="Ex. jane.dough@email.com"
                    variant="outlined"
                    value={email}
                    onChange={handleChange}
                    onBlur={e => handleBlurEmail(e, errors.teamMemberEmails?.[key], handleBlur)}
                    error={
                      touched?.teamMemberEmails?.[key] && Boolean(errors?.teamMemberEmails?.[key])
                    }
                    helperText={touched?.teamMemberEmails?.[key] && errors?.teamMemberEmails?.[key]}
                  />
                </Grid>
              ))}
              <Grid item>
                <Box width="320px" textAlign="left">
                  <Button
                    color="secondary"
                    startIcon={<AddCircleOutline />}
                    onClick={() => handleAddAnother(values, setFieldValue)}
                  >
                    Add another
                  </Button>
                </Box>
              </Grid>
              <Grid item component={Box} marginTop={5} marginBottom={2}>
                <ButtonSubmit variant="contained" color="primary" submitting={isSettingNextStep}>
                  Invite &amp; Finish Setup
                </ButtonSubmit>
              </Grid>
              {errorSettingNextStep && (
                <Grid item>
                  <Typography color="error">
                    Sorry, there was a problem inviting members. Please try again or set it up later
                    from your team management console.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    </SteppedContent>
  )
}

export default StepTeamMembers
